import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges, OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {
    ICoupon,
    IDetailPriceTicket,
    IFareComponentTicket,
    ITicketSector,
    IXId
} from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { ILabels } from "libraries/interfaces/ILabels";
import { Yii } from "libraries/interfaces/Yii";
import { BtnStyleTypes } from "libraries/ui_button/btn.component";
import { Coupon, RuleType, SectorXRefType } from "@afidium/ts-facade";
import { faAngleDown, IconDefinition, faAngleUp } from "@fortawesome/free-solid-svg-icons";

export interface IRuleTypeWithSectorCoupon extends RuleType{
    sectorArrayWithCoupon : ISectorArrayWithCoupon[];
}

export interface ISectorArrayWithCoupon extends SectorXRefType {
    couponArray: Coupon[];
}

@Component({
    selector: 'sectors-air-details-list',
    template: `
        <div *ngFor="let fareComponent of fareComponentArray; let fareComponentIndex = index; trackBy: trackByFn"
             class="ticket-resume">
            <div class="simple-list-container"
                 [class.first]="fareComponentIndex === 0"
                 [class.visible]="visibleRelatedSectors[fareComponentIndex]"
                 (click)="showRelatedSector(fareComponentIndex)">
                <sectors-air-simple-list [sectors]="sectors"
                                         [fareComponentSectors]="fareComponent.sectorArray">
                </sectors-air-simple-list>
                <fa-icon [icon]="visibleRelatedSectors[fareComponentIndex] ? iconAngleUp : iconAngleDown"></fa-icon>
            </div>

            <ng-container *ngIf="visibleRelatedSectors[fareComponentIndex]">

                <div *ngFor="let sector of sectors | sectorFilter: fareComponent.sectorArray ; let i = index; let last = last; trackBy: trackByFn"
                     class="sector-details">
                    <div class="date-details">
                        <sectors-air-coupon-list *ngIf="sector?.coupon"
                                                 [coupon]="sector.coupon"></sectors-air-coupon-list>

                        <div class="ticket-detail-container">
                            <travel-date-begin-end [begin]="sector?.begin" [end]="sector?.end"></travel-date-begin-end>

                            <sector-air-flight-details [sectorData]="sector"
                                                       [detail]="detail"></sector-air-flight-details>
                        </div>
                    </div>

                    <rupture
                            *ngIf="!last && (sector.to.airport.iata !== (sectors | sectorFilter: fareComponent.sectorArray)[i + 1].from.airport.iata)"></rupture>
                </div>
            </ng-container>
            <ng-container *ngIf="visibleRelatedSectors[fareComponentIndex]">
                <div class="fare-component-rules" *permissionIf="fareRuleSearchPermission">
                    <div class="fare-component-rules-actions">
                        <btn [value]="wording.showRules"
                             [type]="btnType.actionLink"
                             (onClick)="changeRulesVisible(!rulesVisible[fareComponentIndex], fareComponentIndex)">
                            <i class="icon-end fa"
                               [class.fa-angle-down]="!rulesVisible[fareComponentIndex]"
                               [class.fa-angle-up]="rulesVisible[fareComponentIndex]"></i>
                        </btn>
                    </div>
                    <div class="fare-component-rules-content" *ngIf="rulesVisible[fareComponentIndex]">
                        <ng-container
                                *ngIf="notesRuleArray | getRelatedRules: (sectors | sectorFilter: fareComponent.sectorArray) ; else noRules">
                            <fare-note
                                    [ruleArray]="(notesRuleArray | getRelatedRules: (sectors | sectorFilter: fareComponent.sectorArray)).detail.ruleArray"></fare-note>
                        </ng-container>
                        <ng-template #noRules>
                            {{wording.noRules}}
                        </ng-template>
                    </div>
                </div>

            </ng-container>

        </div>
    `,
    styles: [`
        .sector-details:first-child {
            margin-top: .75rem;
        }

        :nth-child(odd) .date-details {
            background-color: #fff;
        }

        .sector-details:nth-child(even) .date-details {
            background-color: #f9f8f8;
        }

        .sector-details .date-details {
            padding: .75rem .75rem 0 calc(.75rem + 25px);
            display: flex;
            flex-wrap: nowrap;
        }

        sectors-air-coupon-list {
            margin-right: .75rem;
        }

        .simple-list-container {
            display: flex;
            margin: .75rem .75rem .75rem calc(.75rem + 25px);
            cursor: pointer;
            user-select: none;
        }

        .simple-list-container i {
            margin-left: .75rem;
        }

        .simple-list-container.first {
            margin-top: 0;
        }

        .simple-list-container.visible {
            margin-bottom: 0;
        }

        .fare-component-rules {
            background: #fff;
            padding-left: 35px;
        }

        .fare-component-rules-actions {
            width: 165px;
            margin-right: .75rem;
        }

        .fare-component-rules-content {
            padding: 0 .75rem .75rem 9px;
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectorsAirDetailsListComponent implements OnChanges, OnInit {
    @Input()
    public sectors: ITicketSector[];
    @Input()
    public detail: IDetailPriceTicket;
    @Input()
    public fareComponentSectors: IXId[];

    // All details visibility
    @Input()
    public allVisible: boolean;
    @Input()
    public ruleArray: IRuleTypeWithSectorCoupon[];
    @Output()
    public readonly onChangeAllVisible: EventEmitter<boolean> = new EventEmitter<boolean>();

    public notesRuleArray: IRuleTypeWithSectorCoupon[];

    public readonly btnType: typeof BtnStyleTypes = BtnStyleTypes;
    public displayedSectors: ITicketSector[] = [];
    public visibleRelatedSectors: boolean[] = [];
    public fareComponentArray: IFareComponentTicket[];
    public readonly wording: ILabels = {
        showRules: Yii.t('wui-shared-js', 'Règles tarifaires'),
        noRules: Yii.t('wui-shared-js', 'Aucune note tarifaire'),
    };

    public rulesVisible: boolean[] = [];
    public fareRuleSearchPermission: string = "sasha.booking.detail.air.fareRules.search";


    public ngOnInit(): void {
        if (this.ruleArray) {
            this.notesRuleArray = this.ruleArray.filter((rule: RuleType) => rule.xRole === "FareNote");
        }
    }

    public iconAngleUp: IconDefinition = faAngleUp;
    public iconAngleDown: IconDefinition = faAngleDown;

    public ngOnChanges(changes: SimpleChanges): void {
        this.initFareComponentArray(changes);
        if (changes.allVisible && this.fareComponentArray && this.fareComponentArray.length) {
            this.fareComponentArray
                .forEach((sector: IFareComponentTicket, index: number) => {
                    this.visibleRelatedSectors[index] = this.allVisible;
                });
        }

        if (changes.ruleArray && changes.ruleArray.currentValue) {
            this.notesRuleArray = this.ruleArray.filter((rule: RuleType) => rule.xRole === "FareNote");
        }
    }

    public trackByFn(index: number): number {
        return index;
    }

    public showRelatedSector(index: number): void {
        this.visibleRelatedSectors[index] = !this.visibleRelatedSectors[index];

        //make parent collapser coherent
        const totalSectors: number = this.visibleRelatedSectors.length;
        const totalVisibleSectors: number = this.visibleRelatedSectors.filter((tag: boolean) => tag).length;
        const totalHiddenSectors: number = this.visibleRelatedSectors.filter((tag: boolean) => !tag).length;
        switch (totalSectors) {
            case totalVisibleSectors:
                this.onChangeAllVisible.emit(true);
                break;
            case totalHiddenSectors:
                this.onChangeAllVisible.emit(false);
                break;
            default:
                break;
        }
    }

    public changeRulesVisible(visible: boolean, index: number): void {
        this.rulesVisible[index] = visible;
    }

    private initFareComponentArray(changes: SimpleChanges): void {
        if (changes && changes.detail && changes.detail.currentValue) {
            const fareComponentArrayAll: IFareComponentTicket[] = getFakeFareComponentArrayFromSectors(
                this.sectors
            );

            const fareComponentArray: IFareComponentTicket[] = changes.detail &&
            changes.detail.currentValue &&
            changes.detail.currentValue.rate &&
            changes.detail.currentValue.rate.fareComponentArray ?
                changes.detail.currentValue.rate.fareComponentArray : [];

            this.fareComponentArray = fareComponentArrayAll.map((fakeFareComponent: IFareComponentTicket): IFareComponentTicket => {
                const realFareComponent: IFareComponentTicket = fareComponentArray.find(
                    (fareComponentReal: IFareComponentTicket): boolean => {
                        const id: IXId = fakeFareComponent.sectorArray[0];
                        return Boolean(
                            fareComponentReal.sectorArray.find((sectorItem: ITicketSector) => sectorItem.xRef === id.xRef)
                        );
                    });
                return realFareComponent ? realFareComponent : fakeFareComponent;
            });

            // make the array unique
            this.fareComponentArray = this.fareComponentArray.filter(
                (item: IFareComponentTicket, index: number) => this.fareComponentArray.indexOf(item) === index
            );

        }
    }
}

function getFakeFareComponentArrayFromSectors(sectors: ITicketSector[]): IFareComponentTicket[] {
    return sectors.map(({from, to, xId}: ITicketSector) => ({
        from,
        to,
        class: null,
        sectorArray: [{xRef: xId}],
        fareBasis: {
            code: null
        }
    }));
}
